import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './client.js';
import { ApiError } from './api-error.js';

/**
 * Thin wrapper around TanStack Query's `useQuery` that uses the shared
 * `api.get` client. Provides a clean migration path from the legacy
 * `useApi('/path')` hook to React Query.
 *
 * The query function throws {@link ApiError} on failure so that
 * React Query's `error` object carries structured metadata
 * (code, statusCode, requestId, details).
 *
 * @example
 *   const { data, isLoading, error } = useApiQuery<Store[]>(
 *     ['stores'],
 *     '/api/v1/auth/stores',
 *   );
 */
export function useApiQuery<T>(
  queryKey: readonly unknown[],
  path: string,
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, ApiError>({
    queryKey,
    queryFn: async ({ signal }) => {
      const res = await api.get<T>(path, { signal });
      if (!res.success || !res.data) {
        throw new ApiError(
          res.error ?? 'Request failed',
          res.code ?? 'UNKNOWN',
          0,
          res.requestId,
          res.details,
        );
      }
      return res.data;
    },
    ...options,
  });
}
